<template>
  <div>
    <TopSection :title="$t('uploads') | capitalize">
      <template v-slot:status>
        <div class="mr-3">
          <Status
            :count="states[statusTypes.PENDING] || 0"
            :text="$t('pending') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
          />
          <Status
            :count="states[statusTypes.PROCESSING] || 0"
            :text="$t('processing') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
          />
        </div>
        <div class="mr-3">
          <Status
            :count="states[statusTypes.MATCHED] || 0"
            :text="$t('matched') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
          />
          <Status
            :count="states[statusTypes.ERROR] || 0"
            :text="$t('error') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="red"
          />
        </div>
      </template>

      <template v-slot:filters>
        <div class="flex items-center ml-4">
          <b-button
            size="lg"
            class="ml-2 mr-4"
            variant="primary"
            v-b-modal="'playlistSelectorModal'"
          >
            {{ $t('generateUnmatchedReport') | capitalize }}
          </b-button>
          <b-button
            v-if="user.is_ppl_india_user"
            size="lg"
            class="ml-2 mr-4"
            variant="primary"
            v-b-modal="'playlistSelectorModalPreDist'"
          >
            {{ $t('generatePreDistributionReport') | capitalize }}
          </b-button>
          <form autocomplete="off">
            <DatePicker
              v-model="dateRange"
              :placeholder="$t('select_date_range') | capitalize"
              range
            />
          </form>

          <FilterModule
            :title="$t('filterFiles') | capitalize"
            :statuses="{
              [statusTypes.PENDING]: $t('pending'),
              [statusTypes.PROCESSING]: $t('processing'),
              [statusTypes.MATCHED]: $t('matched'),
              [statusTypes.ERROR]: $t('error')
            }"
            :table="$refs.usageFilesTable"
          />
        </div>
      </template>

      <template v-slot:title__after>
        <SelectedFilters class="ml-3" :table="$refs.usageFilesTable" />
      </template>
    </TopSection>

    <!-- TABLE -->
    <v-server-table
      ref="usageFilesTable"
      url="playlistfiles/"
      :columns="columns"
      :options="options"
      @pagination="onPagination"
      @sorted="onSorted"
      @filter="onFilter"
    >
      <template v-slot:filename="{ row }">
        <div
          v-b-tooltip.hover
          class="table-main-class mb-2 width-large"
          :title="row.processed_url.split('/').pop()"
        >
          {{ row.processed_url.split('/').pop() }}
        </div>
        <div class="table-sub-class">
          {{ row.uploader }}
        </div>
      </template>

      <!-- UPLOAD DATE COLUMN -->
      <template v-slot:upload_date="{ row }">
        <div v-if="row.upload_date && row.upload_date.length">
          <div class="table-main-class">
            {{ row.upload_date | transformDate("MMMM Do YYYY") }}
          </div>
          <div class="table-sub-class text-sm">
            {{ row.upload_date | transformDate("h:mm a") }}
          </div>
        </div>
      </template>

      <!-- STATUS COLUMN-->
      <template v-slot:status="{ row }">
        <Status
          v-if="row.status == statusTypes.PENDING"
          :text="$t('pending') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.PROCESSING"
          :text="$t('processing') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.LANDED"
          :text="$t('landed') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.INGESTED"
          :text="$t('ingested') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.MATCHED"
          :text="$t('matched') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.ERROR"
          :text="$t('error') | capitalize"
          :icon="['fas', 'exclamation-circle']"
          color="red"
          :active="true"
          @click="showErrors(row, true)"
          clickable
        />
      </template>

      <!-- STATS COLUMN -->
      <template v-slot:stats="{ row }">
        <div v-if="row.sheet_states">
          <div
            v-for="(v, k) in row.sheet_states"
            :key="k"
            class="table-sub-class text-sm inline"
            style="opacity:0.8;"
          >
            <div>{{ k }}: {{ v }}</div>
          </div>
        </div>
      </template>

      <!-- MATCHING COLUMN -->
      <template v-slot:matching="{ row }">
        <b-progress
          style="height:12px; width:95px;"
          :value="row.matching | getPercentageValue"
          :max="100"
        />
        <div class="table-sub-class m-1">
          {{ `${ row.matching} ${$t('matched')}` }}
        </div>
      </template>

      <!-- CLAIMING STATUS COLUMN -->

      <template v-slot:claiming_status="{ row }">
        <b-progress
          style="height:12px; width:95px;"
          :value="row.claiming_status | getPercentageValue"
          :max="100"
        />
        <div class="table-sub-class m-1">
          {{ `${ row.claiming_status} ${$t('matched')}` }}
        </div>
      </template>

      <!-- READY FOR DISTRIBUTION COLUMN -->
      <template v-slot:readyForDistribution="{ row }">
        <toggle-button
          :value="row.allocable"
          color="#2ec775"
          :sync="true"
          @change="toggleAllocable(row.id, row.allocable)"
        />
      </template>

      <!-- ACTIONS COLUMN-->
      <template v-slot:actions="{ row }">
        <button
          class="button min-w60 float-right"
          v-if="row.status !== statusTypes.ERROR"
        >
          <spinner v-if="loading === row.id || row.status === statusTypes.PENDING || row.status === statusTypes.PROCESSING" />
          <span v-else-if="row.status === statusTypes.MATCHED" @click="rematch(row)">{{ $t('match') | capitalize }}</span>
          <span v-else></span>
        </button>
      </template>

      <!-- CHILD ROWS -->
      <template v-slot:child_row="{ row }">
        <PlaylistFileSheetTable
          v-if="row.sheets.length"
          :key="row.id"
          :data="row.sheets"
          @showErrors="showErrors"
        />
      </template>
    </v-server-table>

    <!-- FILE ERRORS MODAL -->
    <b-modal
      id="errorsModal"
      ref="errorsModal"
      hide-footer
      hide-header
      size="xl"
    >
      <FileErrorsTable
        :key="currentSheet | get('id')"
        :item="currentSheet"
        module="playlistfilesheet"
        :onlyShowFileLevelErrors="onlyShowFileLevelErrors"
      />
    </b-modal>
    <!-- PLAYLISTS SELECTOR MODAL -->
    <b-modal
      id="playlistSelectorModal"
      ref="playlistSelectorModal"
      hide-footer
      hide-header
      size="xl"
    >
      <PlaylistSelector
        :initial="[]"
        @save="generateUnmatchedReport"
        @close="closeModal('playlistSelectorModal')"
      />
    </b-modal>

    <!-- Pre dist PLAYLISTS SELECTOR MODAL -->
    <b-modal
      id="playlistSelectorModalPreDist"
      ref="playlistSelectorModalPreDist"
      hide-footer
      hide-header
      size="xl"
    >
      <PlaylistSelector
        :initial="[]"
        @save="generatePreDistributionReport"
        @close="closeModal('playlistSelectorModalPreDist')"
        usageType="duration"
      />
    </b-modal>

    <!-- Select update method -->
    <b-modal
      id="selectRematch"
      ref="selectRematch"
      :title="$t('match') | capitalize"
      hide-footer
    >
      <p>{{ $t("defaultMatchingInfo") }}</p>
      <div class="d-flex">
        <b-form-checkbox
          v-model="fullMatch"
        >
          {{ $t("automatchedCheckboxInfo") }}
        </b-form-checkbox>
      </div>
      <div class="d-flex">
        <b-form-checkbox
          v-model="releaseMatch"
        >
          {{ $t("useReleaseMatchingInfo") }}
        </b-form-checkbox>
      </div>
      <div class="flex justify-end">
        <action-button
          variant="primary"
          size="big"
          @click="confirmRematch()"
        >
          {{ $t("match") | capitalize }}
        </action-button>
      </div>
    </b-modal>

  </div>
</template>

<style lang="scss">
  #playlistSelectorModal > .modal-dialog {
    max-width: 1280px !important;
  }
</style>

<script>
import { capitalize, flatten, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapGetters, mapMutations, mapState } from "vuex"
import { ApiCalls } from "@/assets/libs/services"
import PlaylistFileSheetTable from "./PlaylistFileSheetTable.vue"
import { api } from "@/main"
import { listRouteMixin } from "@/utils/mixins"
import moment from "moment"

export default {
  name: "UsageFilesTable",
  mixins: [listRouteMixin],
  components: {
    PlaylistFileSheetTable
  },
  data () {
    return {
      dateRange: "",
      start_date: "",
      end_date: "",
      states: {},
      currentSheet: null,
      onlyShowFileLevelErrors: false,
      loading: false,
      selectedPlaylist: null,
      fullMatch:false,
      releaseMatch: false,
      columns: [
        "filename",
        "upload_date",
        "period",
        "usage_type",
        "status",
        "stats",
        "matchingStats",
        "readyForDistribution",
        "actions"
      ],
      options: {
        sortable: ["processed_url", "upload_date", "period"],
        filterable: true,
        headings: {
          filename: capitalize(this.$t("fileName")),
          upload_date: capitalize(this.$t("uploadDate")),
          period: capitalize(this.$t("period")),
          usage_type: capitalize(this.$t("usageType")),
          status: capitalize(this.$t("status")),
          stats: capitalize(this.$t("ingestionStats")),
          matchingStats: capitalize(this.$t("matchingStats")),
          readyForDistribution: capitalize(this.$t("readyForDistribution")),
          actions: ""
        },
        columnsClasses: {
          filename: "width-large",
          upload_date: "width-medium",
          period: "width-medium",
          usage_type: "width-small",
          uploader: "width-medium",
          status: "width-small",
          stats: "width-medium",
          matchingStats: "width-medium",
          readyForDistribution: "width-small",
        },
        requestFunction (params) {
          const queryParams = { ...params, ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))
          return doRequest(this.url, queryParams)
        },

        responseAdapter: resp => {
          const { get } = this.$options.filters
          if (get(resp, "data.results", null)) {
            this.states = resp.data.states
            const openRows = resp.data.results.map(element => {
              return element.id
            })
            this.$refs.usageFilesTable.$refs.table.openChildRows = openRows
          }

          this.states = get(resp.data, "states", null)

          return responsePaginatedAdapter(resp)
        }
      }
    }
  },
  watch: {
    dateRange (val) {
      this.start_date = this.parse_date(val[0])
      this.end_date = this.parse_date(val[1])
      this.$router.replace({
        name: "usages",
        query: { ...this.$router.query, from: this.start_date, to: this.end_date, page: 1 }
      })
    }
  },
  created () {
    let { status } = this.$route.query
    if (status) this.setStatusFilter(status)

  },
  methods: {
    closeModal (modal) {
      this.$refs[modal].hide()
    },
    generateUnmatchedReport (playlists) {
      let sheetIDs = playlists.reduce((acc, playlist) => acc.concat(playlist.sheets.map(sheet => sheet.id)), [])
      console.log(sheetIDs)

      ApiCalls.generateUnmatchedReport(flatten(sheetIDs))
        .then(() => {
          this.$toasted.global.toast(this.$t("generatedReportWillBeSent"))
        })
        .catch(err => {
          this.$toasted.global.toast(err.response.data[0])
        })
    },
    generatePreDistributionReport (playlists) {
      let sheetIDs = playlists.reduce((acc, playlist) => acc.concat(playlist.sheets.map(sheet => sheet.id)), [])
      console.log(sheetIDs)

      ApiCalls.generatePreDistributionReport(flatten(sheetIDs))
        .then(() => {
          this.$toasted.global.toast(this.$t("generatedReportWillBeSent"))
        })
        .catch(err => {
          this.$toasted.global.toast(err.response.data[0])
        })
    },
    toggleAllocable (id, value) {
      api
        .post(`/playlistfiles/${id}/set_allocable/`, {
          allocable: !value
        })
        .then(() => {
          this.$toasted.global.toast(` ${ this.$t("playlist_set_allocable") } ${ !value }`)
          this.$refs.usageFilesTable.getData()
        })
        .catch(err => {
          this.$toasted.global.toast(err)
        })
    },
    confirmRematch () {
      this.loading = this.selectedPlaylist
      return ApiCalls.rematch(this.selectedPlaylist, this.fullMatch, this.releaseMatch)
        .then(() => {
          this.$refs.usageFilesTable.getData()
          this.$toasted.global.toast(this.$t("playlist_will_be_matched_again"))
        })
        .catch(err => {
          this.$toasted.global.toast(err)
        }).finally(() =>{
          this.loading = false
          this.$bvModal.hide("selectRematch")
          this.fullMatch = false
          this.releaseMatch = false
        })
    },
    rematch (row) {
      this.selectedPlaylist = row.id
      this.$bvModal.show("selectRematch")
    },
    getData () {
      this.$refs.usageFilesTable.getData()
    },
    getMetadata ({ extras }, tag) {
      if (extras) {
        return extras.reduce((current, next) => {
          if (next.tag === tag) {
            current.push(next.value)
          }
          return current
        }, [])
      }
      return []
    },
    onFilter (search) {
      this.options.previousPage = null
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
    parse_date (val) {
      return !val ? "" : this.$options.filters.moment(val).format("YYYY-MM-DD")
    },
    showErrors (row, onlyShowFileLevelErrors=false) {
      this.onlyShowFileLevelErrors = onlyShowFileLevelErrors
      this.currentSheet = row
      this.$refs.errorsModal.show()
    },
    ...mapMutations("ui", ["setStatusFilter"])
  },
  computed: {
    ...mapGetters("soundsys", ["statusTypes"]),
    ...mapState("ui", ["statusFilter"]),
    ...mapState("soundsys", ["user"]),
  },
  mounted () {
    const from = get(this.$route.query, "from", "")
    const to = get(this.$route.query, "to", "")

    const fromIsValid = moment(from, "YYYY-MM-DD", true).isValid()
    const toIsValid = moment(to, "YYYY-MM-DD", true).isValid()

    if(fromIsValid && toIsValid) {
      const fromDate = new Date(from)
      const toDate = new Date(to)

      this.dateRange = [fromDate, toDate]
    }
  },
}
</script>
